import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Play, Calendar, Clock, Headphones } from "lucide-react";
import { Reveal } from "../components/common";
import { publicApi, mediaUrl } from "../lib/api";
import VideoModal, { VideoThumb } from "../components/VideoModal";
import EpisodeDescription from "../components/EpisodeDescription";
import Seo from "../components/Seo";
import ShareRow from "../components/ShareRow";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function PodcastSeriesDetail() {
  const { slug } = useParams();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    setLoading(true);
    publicApi.seriesDetail(slug).then(setSeries).catch(() => setSeries(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center text-slate-400">Loading...</div>;

  if (!series) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display font-extrabold text-3xl text-navy mb-4">Series not found</h1>
        <Link to="/podcast" className="text-orange font-bold inline-flex items-center gap-2"><ArrowLeft size={16} /> Back to podcast</Link>
      </div>
    );
  }

  const episodes = series.episodes || [];

  const epTime = (e) => new Date(e.date || e.release_date || 0).getTime();
  const newestId = episodes.reduce((best, e) => (epTime(e) > (best ? epTime(best) : -Infinity) ? e : best), null)?.id;
  const isFresh = (e) => e.id === newestId;

  return (
    <div>
      <Seo
        title={series.title}
        description={series.description || `${series.title} — a podcast series from Cloud Accounting Support Services.`}
        type="article"
      />
      {active && <VideoModal videoId={active.youtubeId} title={active.title} onClose={() => setActive(null)} />}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <Link to="/podcast" className="inline-flex items-center gap-2 text-slate-300 hover:text-orange text-sm font-semibold mb-6 transition-colors"><ArrowLeft size={16} /> All podcasts</Link>
          <span className="text-orange font-bold text-sm tracking-widest uppercase">Podcast Series</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3">{series.title}</h1>
          {series.description && <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">{series.description}</p>}
          <ShareRow
            className="mt-6 [&_span]:text-slate-400 [&_button]:text-slate-300 [&_button:hover]:text-white [&_button:hover]:bg-white/10"
            path={`/podcast/series/${series.slug}`}
            title={series.title}
            label="Share this series"
          />
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {episodes.length === 0 ? (
            <div className="text-center text-slate-400 py-16"><Headphones size={40} className="mx-auto mb-4 text-slate-300" />No episodes in this series yet.</div>
          ) : (
            <div className="space-y-6">
              {episodes.map((p, i) => (
                <Reveal key={p.id} delay={i * 60}>
                  <button type="button" data-testid={`series-episode-${p.id}`} onClick={() => setActive(p)} className="w-full text-left group grid md:grid-cols-[280px_1fr] gap-6 bg-slate-50 rounded-2xl overflow-hidden border border-slate-100 hover:shadow-lg transition-all">
                    <div className="relative aspect-video overflow-hidden bg-navy self-center">
                      <VideoThumb src={p.image} youtubeId={p.youtubeId} fallback={series.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-navy/40 flex items-center justify-center">
                        <span className="w-14 h-14 rounded-full bg-orange flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl"><Play size={22} className="text-white ml-1" fill="white" /></span>
                      </div>
                    </div>
                    <div className="p-6 md:py-8 md:pr-8">
                      <div className="flex items-center gap-2 mb-3">
                        {p.episode && <span className="inline-block bg-orange/10 text-orange text-xs font-bold px-3 py-1 rounded-full">{p.episode}</span>}
                        {isFresh(p) && (
                          <span data-testid={`episode-new-badge-${p.id}`} className="inline-flex items-center gap-1.5 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> New
                          </span>
                        )}
                      </div>
                      <h3 className="font-display font-bold text-xl md:text-2xl text-navy leading-snug mb-3 group-hover:text-orange transition-colors">{p.title}</h3>
                      <EpisodeDescription text={p.description} className="text-slate-500 leading-relaxed mb-4" />
                      <div className="flex items-center gap-5 text-xs text-slate-400">
                        <span className="flex items-center gap-1.5"><Calendar size={13} /> {formatDate(p.date)}</span>
                        <span className="flex items-center gap-1.5"><Clock size={13} /> {p.duration}</span>
                      </div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
